import { Link } from 'react-router';

import { getDocsUrl } from '@cartwhl/common/utils';
import TextLegacy from '@cartwhl/ui/base/Text';
import Text from '@cartwhl/ui/base/TextNew';
import BlenderIcon from '@cartwhl/ui/icons/BlenderIcon';
import MayaIcon from '@cartwhl/ui/icons/MayaIcon';
import UnityIcon from '@cartwhl/ui/icons/UnityIcon';
import UnrealIcon from '@cartwhl/ui/icons/UnrealIcon';

import { formatPluginInterestLabel } from 'components/NotifyMeModal/formatNotifyMeInterest';

import {
  type PluginConfig,
  type PluginLogoComponent,
  pluginFeatures,
  plugins,
} from '../integrationsPageData';
import PluginCard from './PluginCard';
import PluginFeaturesSection from './PluginFeaturesSection';

function getPluginDocumentationUrl(plugin: PluginConfig): string | undefined {
  if (!plugin.documentationPath) {
    return undefined;
  }

  return getDocsUrl(plugin.documentationPath);
}

function getPluginInterestLabel(plugin: PluginConfig): string | undefined {
  if (plugin.status !== 'coming-soon') {
    return undefined;
  }

  return formatPluginInterestLabel(plugin.name);
}

const pluginLogos: Record<string, PluginLogoComponent> = {
  'unreal-engine': UnrealIcon,
  'autodesk-maya': MayaIcon,
  unity: UnityIcon,
  blender: BlenderIcon,
};

export default function IntegrationsPage() {
  return (
    <main className="mb-32 flex flex-col gap-16 min-[960px]:gap-20">
      <div className="container flex flex-col gap-10">
        <div className="flex flex-col gap-20 pt-10 min-[960px]:pt-[80px]">
          <Text
            as="h1"
            token={{ base: 'display/36/bold', lg: 'display/64/bold' }}
            className="text-pretty"
          >
            3D Plugins
          </Text>
          <div className="flex max-w-[920px] flex-col gap-6">
            <Text
              as="p"
              token={{ base: '20/medium', lg: '36/medium' }}
              className="text-pretty"
            >
              Leverage Cartwheel&apos;s Capabilities where you already work
            </Text>
            <TextLegacy
              as="p"
              variant="web/body/24"
              className="text-secondary mt-6 text-pretty"
            >
              Plugins require an{' '}
              <Link
                to="/pricing"
                className="text-blue font-inherit inline underline transition-opacity hover:opacity-80"
              >
                Individual Pro or Team Pro account
              </Link>{' '}
              — you&apos;ll use your plan&apos;s API key to power performance
              capture, motion generation, and retargeting.
            </TextLegacy>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 min-[720px]:grid-cols-2 min-[960px]:grid-cols-4">
          {plugins.map((plugin) => {
            const Logo = pluginLogos[plugin.id];
            if (!Logo) {
              return null;
            }

            const documentationUrl = getPluginDocumentationUrl(plugin);

            return (
              <div key={plugin.id} className="flex flex-col gap-3">
                <PluginCard
                  plugin={plugin}
                  interestLabel={getPluginInterestLabel(plugin)}
                  Logo={Logo}
                />
                <div className="h-5 shrink-0 text-center">
                  {documentationUrl ? (
                    <a
                      href={documentationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue text-[14px]/[14px] font-medium underline transition-opacity hover:opacity-80"
                    >
                      View documentation
                    </a>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <PluginFeaturesSection features={pluginFeatures} />
    </main>
  );
}
